import { ArrowRight, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useProfileGate } from './ProfileCompletionGate';

const DISMISS_KEY = 'qh:profile_banner_dismissed';

/**
 * Phase 7-dashboard hotfix round 3 — route-scoped profile-completion nudge.
 *
 * Visible only on the surfaces where applying / browsing jobs is the immediate intent:
 *   - /job-seekers and /job-seekers/:slug
 *   - /dashboard/* (signed-in dashboard area)
 *
 * Explicitly NOT shown on the home page, marketing pages (/about, ...),
 * or any auth surface (/signin, /signup, /verify-email/...). The earlier "show
 * everywhere" rule fired on home and felt like a guilt-trip; the new scope only
 * surfaces the prompt when the candidate is in a job-seeking context.
 *
 * Click "Complete now" → opens the soft modal. The X dismisses for the rest of the
 * browser session (sessionStorage flag); the next session re-shows it.
 */
export function ProfileCompletionBanner() {
  const { candidate } = useAuth();
  const { openSoft } = useProfileGate();
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.sessionStorage.getItem(DISMISS_KEY) === '1';
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (dismissed) window.sessionStorage.setItem(DISMISS_KEY, '1');
  }, [dismissed]);

  if (!candidate || candidate.profile_completed || dismissed) return null;
  if (!isBannerRoute(pathname)) return null;

  return (
    <div role="status" className="mx-auto mt-4 mb-2 max-w-[1180px] px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between gap-3 rounded-[3px] border border-[#0b5c22]/20 bg-[#e7f5ea] px-4 py-3 sm:px-5">
        <p className="min-w-0 flex-1 text-sm font-medium text-[#0b5c22]">
          Upload your resume and add basic info to apply for jobs.
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => openSoft()}
            className="inline-flex items-center gap-1.5 rounded-[2px] bg-[#14150f] px-3 py-2 font-['JetBrains_Mono',monospace] text-[10px] font-medium uppercase tracking-[0.04em] text-[#f5f6f2] transition-colors hover:bg-[#0b5c22] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1caa3c]"
          >
            Complete now
            <ArrowRight className="w-3 h-3" />
          </button>
          <button
            type="button"
            aria-label="Dismiss for this session"
            onClick={() => setDismissed(true)}
            className="p-1 text-[#6b6f68] transition-colors hover:text-[#14150f] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1caa3c]"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function isBannerRoute(pathname: string): boolean {
  if (pathname === '/job-seekers' || pathname.startsWith('/job-seekers/')) return true;
  if (pathname === '/dashboard' || pathname.startsWith('/dashboard/')) return true;
  return false;
}
